import {
  Button,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  cn,
} from "@dd/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Link,
  Outlet,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import {
  Armchair,
  CalendarDays,
  ClipboardList,
  FlaskConical,
  LayoutDashboard,
  LogOut,
  Menu,
  Pill,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "../../lib/api.ts";
import { fetchSessionUser, getUser, signOut } from "../../lib/auth.ts";
import { keys } from "../../lib/query.ts";
import { RondaLogo } from "../ronda-logo.tsx";

/**
 * Las secciones del panel de pacientes.
 *
 * Misma forma que las del consultorio —dos grupos, un renglón por pantalla—
 * porque es el mismo producto y no hay razón para que se navegue distinto. Lo
 * que cambia es qué hay adentro: acá nadie gestiona una agenda ajena.
 *
 * "En el consultorio" va aparte de "Mi salud" a propósito: son dos momentos
 * distintos. Mi salud se mira desde el sillón de casa, en cualquier momento;
 * la sala de espera se usa parada en la puerta, una vez, y por eso no puede
 * quedar perdida entre las otras.
 */
const NAV_GROUPS = [
  {
    label: "Mi salud",
    items: [
      { to: "/portal", label: "Inicio", icon: LayoutDashboard },
      { to: "/portal/turnos", label: "Mis turnos", icon: CalendarDays },
      { to: "/portal/estudios", label: "Mis estudios", icon: FlaskConical },
      { to: "/portal/medicacion", label: "Mi medicación", icon: Pill },
      {
        to: "/portal/indicaciones",
        label: "Mis indicaciones",
        icon: ClipboardList,
      },
    ],
  },
  {
    label: "En el consultorio",
    items: [{ to: "/portal/sala", label: "Sala de espera", icon: Armchair }],
  },
] as const;

type PortalPath = (typeof NAV_GROUPS)[number]["items"][number]["to"];

/**
 * La cáscara del panel de pacientes.
 *
 * Reusa los mismos componentes de barra lateral que el del consultorio, para
 * que las dos mitades del producto se vean como una. No trae el selector de
 * sede ni la paleta de comandos: son herramientas de quien administra una
 * clínica, y acá no habría qué poner adentro.
 */
export function PortalShell() {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const client = useQueryClient();
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const { data: user } = useQuery({
    queryKey: keys.sessionUser,
    queryFn: fetchSessionUser,
    initialData: getUser,
    initialDataUpdatedAt: 0,
    staleTime: 5 * 60 * 1000,
  });

  /** Inicio sólo se enciende en su propia ruta: si no, se enciende siempre. */
  function isActive(to: PortalPath) {
    return to === "/portal" ? pathname === "/portal" : pathname.startsWith(to);
  }

  /**
   * Retoma la invitación que quedó a medias.
   *
   * Quien abre el enlace sin cuenta va a crearla y vuelve acá. Sin esto
   * tendría que volver al correo a buscar el enlace otra vez, que es
   * exactamente el paso donde la gente abandona.
   */
  useEffect(() => {
    const pendiente = (() => {
      try {
        return localStorage.getItem("dd-portal-invite");
      } catch {
        return null;
      }
    })();
    if (!pendiente) return;
    void api.portal
      .acceptInvite(pendiente)
      .catch(() => {
        // Vencida, ya usada, o de otra persona. No hay nada que avisar acá: el
        // portal muestra igual lo que esta cuenta sí tiene atado.
      })
      .finally(() => {
        try {
          localStorage.removeItem("dd-portal-invite");
        } catch {
          // Ídem: no poder olvidarla no rompe nada, sólo la reintenta.
        }
        void client.invalidateQueries({ queryKey: keys.portalOverview });
      });
  }, [client]);

  function leave() {
    signOut();
    void navigate({ to: "/paciente" });
  }

  const nav = (
    <nav className="space-y-4">
      {NAV_GROUPS.map((group) => (
        <div key={group.label}>
          <p className="mb-1.5 px-2.5 text-[10px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
            {group.label}
          </p>
          <SidebarMenu>
            {group.items.map(({ to, label, icon: Icon }) => (
              <SidebarMenuItem key={to}>
                <SidebarMenuButton asChild isActive={isActive(to)}>
                  <Link to={to} onClick={() => setMenuOpen(false)}>
                    <Icon />
                    <span>{label}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </div>
      ))}
    </nav>
  );

  return (
    <div className="flex min-h-dvh bg-background">
      <aside className="sticky top-0 z-20 hidden h-screen w-[200px] shrink-0 flex-col border-r border-border bg-card md:flex">
        <div className="flex h-14 items-center border-b border-border px-4">
          <Link to="/portal" aria-label="Ir al inicio">
            <RondaLogo />
          </Link>
        </div>
        <div className="flex flex-1 flex-col p-3">
          {nav}
          <div className="mt-auto border-t border-border pt-3">
            <p className="truncate px-2.5 text-xs text-muted-foreground">
              {user?.name}
            </p>
            <Button
              variant="ghost"
              size="sm"
              className="mt-1 w-full justify-start"
              onClick={leave}
            >
              <LogOut className="h-4 w-4" />
              Salir
            </Button>
          </div>
        </div>
      </aside>

      <main className="min-w-0 flex-1">
        {/* En el teléfono la barra se guarda detrás del menú: el portal se usa
            casi siempre desde ahí, y una lateral fija se comería la pantalla. */}
        <div className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-border bg-card/95 px-4 backdrop-blur md:hidden">
          <Button
            variant="ghost"
            size="icon"
            aria-label="Abrir menú"
            onClick={() => setMenuOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </Button>
          <RondaLogo />
          <Button
            variant="ghost"
            size="icon"
            aria-label="Salir"
            onClick={leave}
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>

        {menuOpen && (
          <div className="fixed inset-0 z-50 md:hidden">
            <button
              type="button"
              className="absolute inset-0 bg-foreground/40"
              aria-label="Cerrar menú"
              onClick={() => setMenuOpen(false)}
            />
            <aside className="relative flex h-full w-[min(86vw,300px)] flex-col overflow-y-auto bg-card p-4 shadow-xl">
              <div className="mb-4 flex items-center justify-between">
                <RondaLogo />
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Cerrar menú"
                  onClick={() => setMenuOpen(false)}
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
              {nav}
            </aside>
          </div>
        )}

        <div className={cn("mx-auto max-w-3xl px-5 py-8 md:px-8")}>
          <Outlet />
        </div>
      </main>
    </div>
  );
}
